<template>
  <div v-if="editorError">
    <form-textarea
      name="json-editor"
      data-qa="json-editor-textarea"
      :label="label"
      :is-update-in-process="isUpdateInProcess"
      v-model:inputValue="fallbackValue"
      @update:inputValue="updateFallbackValue"
      rows="20"
    >
    </form-textarea>
  </div>
  <div v-else>
    <label class="rbz-label" :class="{ disabled: isUpdateInProcess }">
      {{ label }}
    </label>
    <div ref="editorContainer" class="editor" data-qa="json-editor-component"></div>
  </div>
</template>
<script setup lang="ts">
import JSONEditor from 'jsoneditor';
import { ref, watch, onMounted, onBeforeUnmount } from 'vue';
import FormTextarea from './FormTextarea.vue';

interface JsonField {
  label: string;
  classes?: string;
  inputValue?: string | null;
  isUpdateInProcess?: boolean;
}

const props = withDefaults(defineProps<JsonField>(), {
  classes: '',
  inputValue: null,
});

interface jsonFieldEmits {
  (e: 'update:inputValue', value: string | number): void;

  (e: 'invalid', value: boolean): void;
}

const emit = defineEmits<jsonFieldEmits>();

const editorContainer = ref(null);
const editorError = ref(false);
const fallbackValue = ref(props.inputValue || '');
let editor: JSONEditor | null = null;

watch(
  () => props.inputValue,
  (newValue) => {
    if (editor && newValue !== null) {
      try {
        const currentJson = editor.get();
        if (newValue !== JSON.stringify(currentJson)) {
          editor.update(JSON.parse(newValue));
          emit('invalid', false);
        }
      } catch (_e) {
        emit('invalid', true);
      }
    } else if (editorError.value) {
      fallbackValue.value = newValue || '';
    }
  }
);

const updateFallbackValue = (updateValue: string | number) => {
  const stringValue = String(updateValue);
  fallbackValue.value = stringValue;
  emit('update:inputValue', stringValue);
};

onMounted(() => {
  try {
    editor = new JSONEditor(editorContainer.value, {
      modes: ['code', 'tree'],
      onChange: () => {
        try {
          const updatedValue = JSON.stringify(editor.get());
          emit('update:inputValue', updatedValue);
          emit('invalid', false);
        } catch (_e) {
          emit('invalid', true);
        }
      },
      onError: () => {
        emit('invalid', true);
      },
      onEditable: () => {
        return !props.isUpdateInProcess;
      },
    });
    if (props.inputValue) {
      try {
        editor.update(JSON.parse(props.inputValue));
        emit('invalid', false);
      } catch (_e) {
        emit('invalid', true);
      }
    }
  } catch (_e) {
    editorError.value = true;
  }
});

onBeforeUnmount(() => {
  if (editor) {
    if (typeof editor.destroy === 'function') {
      editor.destroy();
    }
    emit('invalid', false);
  }
});
</script>
<style scoped lang="scss">
@use '../../assets/styles/colors' as colors;
@use 'sass:map';

.editor {
  height: 500px;
  margin-bottom: 32px;
  width: 100%;
}

// Design for the json editor
:deep(.jsoneditor-menu) {
  background: map.get(colors.$colors, 'nobel');

  button {
    float: none;
  }

  .jsoneditor-menu {
    background: map.get(colors.$colors, 'white');
  }

  .jsoneditor-selected {
    background: map.get(colors.$colors, 'boulder');
  }
}

:deep(.jsoneditor-contextmenu) {
  z-index: 5;
}

/* stylelint-disable-next-line selector-class-pattern */
:deep(.jsoneditor-poweredBy) {
  display: none;
}

:deep(.jsoneditor-outer.has-status-bar) {
  margin-bottom: -26px !important;
  padding-bottom: 26px !important;
}
</style>
